import React, { useState, useEffect } from 'react';
import { Book } from '../types';
import { getImageUrl, getThumbnailUrl } from '../services/api';
import './BookGrid.css';

export interface BookGridProps {
  books: Book[];
  onBookClick: (book: Book) => void;
  isLoading?: boolean;
  emptyMessage?: string;
}

export function BookGrid({
  books,
  onBookClick,
  isLoading = false,
  emptyMessage = 'Nenhum projeto encontrado.',
}: BookGridProps): React.ReactElement {
  if (isLoading) {
    return (
      <div className="book-grid book-grid--loading">
        <div className="book-grid__loading-container">
          <div className="book-grid__spinner" />
          <p className="book-grid__loading-text">Carregando projetos...</p>
        </div>
      </div>
    );
  }

  if (books.length === 0) {
    return (
      <div className="book-grid book-grid--empty">
        <div className="book-grid__empty-container">
          <span className="book-grid__empty-icon">📦</span>
          <p className="book-grid__empty-text">{emptyMessage}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="book-grid">
      <div className="book-grid__container">
        {books.map((book) => (
          <BookGridItem
            key={book.id}
            book={book}
            onClick={() => onBookClick(book)}
          />
        ))}
      </div>
    </div>
  );
}

interface BookGridItemProps {
  book: Book;
  onClick: () => void;
}

function BookGridItem({ book, onClick }: BookGridItemProps): React.ReactElement {
  const images = book.images && book.images.length > 0 ? book.images : [];
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    if (images.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentImageIndex(prev => (prev + 1) % images.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [images.length]);

  const hasImages = images.length > 0;

  const maxVisibleTags = 3;
  const visibleTags = book.tags?.slice(0, maxVisibleTags) || [];
  const remainingTagsCount = (book.tags?.length || 0) - maxVisibleTags;

  return (
    <article
      className="book-grid__item"
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      aria-label={`Ver detalhes de ${book.title}`}
    >
      <div className="book-grid__image-container">
        {hasImages ? (
          images.map((img, index) => (
            <img
              key={img.id}
              // Grade usa thumbnail (300x400): a imagem cheia (até 1920x2400)
              // é dezenas de vezes mais pesada e só é necessária no modal
              src={getThumbnailUrl(img.filepath)}
              alt={`Capa de ${book.title}`}
              className={`book-grid__image ${index === currentImageIndex ? 'book-grid__image--visible' : ''}`}
              loading="lazy"
              onError={(e) => {
                // Imagens importadas do WordPress antes das thumbnails existirem
                // não têm thumb_ em disco — cai para o arquivo cheio (que existe)
                const el = e.target as HTMLImageElement;
                if (el.src !== getImageUrl(img.filepath)) {
                  el.src = getImageUrl(img.filepath);
                }
              }}
            />
          ))
        ) : null}
        <div
          className="book-grid__image-placeholder"
          style={{ display: hasImages ? 'none' : 'flex' }}
        >
          <span className="book-grid__placeholder-icon">📦</span>
          <span className="book-grid__placeholder-text">Sem imagem</span>
        </div>

        {book.physicallyAvailable && (
          <div
            className="book-grid__availability-badge"
            title="Disponível fisicamente"
            aria-label="Projeto disponível fisicamente"
          >
            <span className="book-grid__availability-icon">✓</span>
            <span className="book-grid__availability-text">Disponível</span>
          </div>
        )}

        {book.isAwarded && (
          <div
            className="book-grid__awarded-badge"
            title="Projeto Premiado"
            aria-label="Projeto premiado"
          >
            <span className="book-grid__awarded-icon">🏆</span>
          </div>
        )}

        {images.length > 1 && (
          <div className="book-grid__image-dots">
            {images.map((_, index) => (
              <span
                key={index}
                className={`book-grid__image-dot ${index === currentImageIndex ? 'book-grid__image-dot--active' : ''}`}
              />
            ))}
          </div>
        )}
      </div>

      <div className="book-grid__info">
        <h3 className="book-grid__title" title={book.title}>
          {book.title}
        </h3>

        {book.publicationYear && (
          <span className="book-grid__year">{book.publicationYear}</span>
        )}

        {visibleTags.length > 0 && (
          <div className="book-grid__tags">
            {visibleTags.map((tag) => (
              <span key={tag.id} className="book-grid__tag" title={tag.name}>
                {tag.name}
              </span>
            ))}
            {remainingTagsCount > 0 && (
              <span className="book-grid__tag book-grid__tag--more">
                +{remainingTagsCount}
              </span>
            )}
          </div>
        )}
      </div>
    </article>
  );
}

export default BookGrid;
